// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState, useEffect, SyntheticEvent, useMemo } from 'react';
import { useRouter } from 'next/router';

import Email from 'domains/models/user/email';
import { ErrorList } from 'domains/models';
import {
  ProviderType,
  Provider,
  providerTypeToProvider,
} from 'domains/models/account/providerType';
import { microsoftAuthUrlWithEmail } from 'integrations/microsoft/presentations/views';
import { googleAuthUrlWithEmail } from 'integrations/google/presentations/views';
import { get, post } from 'infras/rest_client';
import { TeamCreateBody, TeamListItem } from 'presentations/api/schema/team';
import { UserItem } from 'presentations/api/schema/user';
import {
  apiPath,
  LoginDefaultStep,
  onboardPath,
} from 'presentations/routes/path';
import * as tracker from 'presentations/views/helpers/analytics';
import {
  signInWithSlack,
  createSessionWithCustomToken,
  createSessionWithCredential,
  getStartSessionUrl,
  startTeamSession,
  TokenType,
  createSessionWithEmailAndPassword,
  signInWithApple,
} from 'presentations/views/helpers/auth';
import { authorize } from 'presentations/views/helpers/auth/session';
import firebase from 'infras/firebase/client';

import { SeverityType } from 'presentations/views/components/atoms/Snackbar';

interface TokenQuery {
  customToken?: string | null;
  idToken?: string | null;
  providerType?: string | number | null;
  credential?: firebase.auth.OAuthCredential | null;
}

export enum Steps {
  Lookup = 1,
  TeamSelect = 2,
  Login = 3,
  Starting = 4,
  JoinTeam = 5,
}

export interface Props {
  locale: string;
  step?: number;
  to: string;
  googleUrl: string;
  microsoftUrl: string;
  isTrackSignup: boolean;
  teams?: TeamListItem[];
  joinTeams?: TeamListItem[];
  email?: string | null;
  isMobileAppUser: boolean;
  errorCode: string | null;
}

const defaultStepJoinTeam = 'joinTeam';

export const useLoginPage = (props: Props) => {
  const t = translateInComponent(translations);

  const [loadingProvider, setLoadingProvider] = useState<Provider | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResetLoading, setIsResetLoading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<Steps>(
    props.step || Steps.Lookup,
  );
  const [providers, setProviders] = useState<string[] | null>(null);
  const [email, setEmail] = useState<string | null>(props.email || null);
  const [password, setPassword] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorList>({});
  const [teams, setTeams] = useState<TeamListItem[]>(props.teams || []);

  const [joinTeams, setJoinTeams] = useState<TeamListItem[]>(
    props.joinTeams || [],
  );
  const [teamName, setTeamName] = useState<string | null>(null);
  const [isCheckedAutoJoin, setIsCheckedAutoJoin] = useState<boolean>(true);

  const [tokenType, setTokenType] = useState<TokenType | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [providerType, setProviderType] = useState<ProviderType | null>(null);
  const [viewer, setViewer] = useState<UserItem | null>(null);

  const { query } = useRouter();
  const [message, setMessage] = useState<string | null>(null);
  const [severity, setSeverity] = useState<SeverityType>(SeverityType.Success);

  const [viewerDomain, setViewerDomain] = useState<string | null>(null);

  useEffect(() => {
    if (query.defaultStep === String(LoginDefaultStep.JoinTeam)) {
      loginForJoinTeam();
    } else {
      const tokenQuery: TokenQuery = {
        customToken: query.customToken ? String(query.customToken) : null,
        idToken: query.idToken ? String(query.idToken) : null,
        providerType: query.providerType ? String(query.providerType) : null,
        credential: null,
      };
      loginWithToken(tokenQuery);
    }
  }, []);

  useEffect(() => {
    if (!viewer?.name) return;
    setTeamName(t('default team name', { name: viewer.name }));
  }, [viewer]);

  useEffect(() => {
    if (!props.errorCode) {
      return;
    }

    let message = null;
    if (props.errorCode === 'invalid_grant') {
      message = translate('errors.Auth InvalidGrant');
    } else if (props.errorCode === 'unknown_error') {
      message = translate('errors.Auth UnknownError');
    }
    setSeverity(SeverityType.Error);
    setMessage(message);
  }, []);

  async function loginWithToken(tokenQuery: TokenQuery) {
    const { customToken, idToken, providerType, credential } = tokenQuery;
    if (!customToken && !idToken) return;

    let tokenUser: UserItem | null = null;
    let token: string | null = null;
    let tokenType: TokenType | null = null;
    setActiveStep(Steps.Starting);
    setIsLoading(true);
    const type = Number(providerType);
    setLoadingProvider(providerTypeToProvider(type));
    setProviderType(type);

    if (customToken) {
      token = String(customToken);
      tokenType = TokenType.CustomToken;
      tokenUser = await createSessionWithCustomToken(token, props.locale);
    } else if (idToken) {
      token = String(idToken);
      tokenType = TokenType.IdToken;
      tokenUser = await createSessionWithCredential(
        token,
        type,
        credential,
        props.locale,
      );
    }
    if (!tokenUser || !token || !tokenType) {
      return;
    }
    setTokenType(tokenType);
    setToken(token);
    setViewer(tokenUser);
    const teamListItems = (await fetchTeams()) || [];
    setTeams(teamListItems);

    if (teamListItems.length === 0) {
      const joinTeams = await fetchLookupTeams();
      setJoinTeams(joinTeams);
      setActiveStep(Steps.JoinTeam);
      setIsLoading(false);
      return;
    } else if (teamListItems.length !== 1) {
      setActiveStep(Steps.TeamSelect);
      setIsLoading(false);
      return;
    }
    const team = teamListItems[0];
    const ret = await startTeamSession(team.uid);
    if (!ret) {
      console.log('start team session failed');
      return;
    }
    tracker.login(type, viewer);
    const url = getStartSessionUrl({
      to: props.to,
      teamUid: team.uid,
      providerType: type,
      tokenType,
      token,
    });
    window.location.href = url;
  }

  async function loginForJoinTeam() {
    setActiveStep(Steps.Starting);
    setIsLoading(true);

    const viewer = await authorize();
    if (!viewer) {
      setActiveStep(Steps.Lookup);
      setIsLoading(false);
      return;
    }
    setViewer(viewer);

    const teamListItems = (await fetchTeams()) || [];
    setTeams(teamListItems);
    if (teamListItems.length === 0) {
      const joinTeams = await fetchLookupTeams();
      setJoinTeams(joinTeams);
      setActiveStep(Steps.JoinTeam);
      setIsLoading(false);
      return;
    } else if (teamListItems.length !== 1) {
      setActiveStep(Steps.TeamSelect);
      setIsLoading(false);
      return;
    }

    const team = teamListItems[0];
    const ret = await startTeamSession(team.uid);
    if (!ret) {
      console.log('start team session failed');
      return;
    }
    window.location.href = props.to;
  }

  async function onSubmitEmailPassword(e: React.FormEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (!email) {
      return;
    }

    const newErrors: ErrorList = {};
    if (!password) {
      newErrors['password'] = ['model.User.password.E001'];
      setErrors(newErrors);
      return;
    }
    setProviderType(ProviderType.Password);
    setTokenType(TokenType.Password);
    setIsLoading(true);
    const viewer = await createSessionWithEmailAndPassword(
      email,
      password,
      props.locale,
    );
    if (!viewer) {
      setIsLoading(false);
      setMessage(t('Error Login'));
      setSeverity(SeverityType.Error);
      return;
    }
    setViewer(viewer);

    const teamListItems = (await fetchTeams()) || [];
    setTeams(teamListItems);

    if (teamListItems.length === 0) {
      const joinTeams = await fetchLookupTeams();
      setJoinTeams(joinTeams);
      const domain = await fetchValidatedDomain(viewer);
      setViewerDomain(domain);
      setActiveStep(Steps.JoinTeam);
      setIsLoading(false);
      return;
    } else if (teamListItems.length !== 1) {
      setActiveStep(Steps.TeamSelect);
      setIsLoading(false);
      return;
    }
    const team = teamListItems[0];
    const ret = await startTeamSession(team.uid);
    if (!ret) {
      setIsLoading(false);
      return;
    }
    const type = ProviderType.Password;
    if (props.isTrackSignup) {
      tracker.signUp(type, viewer);
    } else {
      tracker.login(type, viewer);
    }
    const url = getStartSessionUrl({
      to: props.to,
      teamUid: team.uid,
      providerType: type,
      tokenType: TokenType.Password,
      email,
      password,
    });
    window.location.href = url;
  }

  async function fetchLookupTeams() {
    const result = await get(apiPath(`/v1/teams/lookup`));
    if (!result.isSuccess) {
      return [];
    }
    const listItems: TeamListItem[] = result.data;
    if (!listItems || listItems.length === 0) {
      return [];
    }
    return listItems;
  }

  async function fetchValidatedDomain(
    viewer: UserItem,
  ): Promise<string | null> {
    const domain = (viewer?.email || '').split('@')[1] || null;
    if (!domain) {
      return null;
    }

    const result = await post(apiPath('/v1/teams/validate_domains'), {
      data: {
        teamDomains: [domain],
      },
    });
    if (!result.isSuccess) {
      return null;
    }

    const validatedDomains: string[] = result.data;
    return validatedDomains[0] || null;
  }

  async function fetchTeams() {
    const result = await get(apiPath(`/v1/teams`));
    if (!result.isSuccess) {
      return null;
    }
    const listItems: TeamListItem[] = result.data;
    if (!listItems || listItems.length === 0) {
      return null;
    }
    return listItems;
  }

  async function onSubmitEmailLookup(e: React.FormEvent<HTMLButtonElement>) {
    e.preventDefault();

    const newEmail = new Email(email);
    const newErrors: ErrorList = {};
    if (!newEmail.isValid()) {
      newErrors['email'] = newEmail.errors['email'];
    }
    setErrors(newErrors);
    if (Object.entries(newErrors).length > 0) {
      return;
    }

    const data = { email };
    setIsLoading(true);
    const result = await post(apiPath('/v1/user/provider'), { data });
    if (!result.isSuccess) {
      setIsLoading(false);
      setErrors(result.errors);
      setMessage(t('Error'));
      setSeverity(SeverityType.Error);
      return;
    }
    setActiveStep(Steps.Login);
    setProviders(result.data.providers);
    setIsLoading(false);
  }

  const onChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.currentTarget;
    const newErrors: ErrorList = {};

    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'teamName':
        setTeamName(value);
        break;
      case 'autoJoinTeam':
        setIsCheckedAutoJoin(checked);
        break;

      default:
        break;
    }
    setErrors(newErrors);
  };

  const onClickService = async (
    e: SyntheticEvent<HTMLInputElement>,
    provider: Provider,
  ) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setLoadingProvider(provider);
      switch (provider) {
        case Provider.Google: {
          window.location.href = googleAuthUrlWithEmail(props.googleUrl, email);
          break;
        }
        case Provider.Microsoft: {
          window.location.href = microsoftAuthUrlWithEmail(
            props.microsoftUrl,
            email,
          );
          break;
        }
        case Provider.Apple: {
          const result = await signInWithApple();
          if (!result || !result.credential) break;
          const credential: firebase.auth.OAuthCredential = result.credential;
          const tokenQuery: TokenQuery = {
            customToken: null,
            idToken: credential.idToken,
            providerType: ProviderType.Apple,
            credential,
          };
          loginWithToken(tokenQuery);
          break;
        }
        case Provider.Slack: {
          signInWithSlack();
          break;
        }
        default:
          setIsLoading(false);
          break;
      }
    } catch (error) {
      console.debug(error);
      setIsLoading(false);
      setLoadingProvider(null);
    }
  };

  const onClickTeam = async (team: TeamListItem) => {
    if (!providerType) {
      console.log('providerType is not exist.');
      return;
    }
    setIsLoading(true);
    const ret = await startTeamSession(team.uid);
    if (!ret) {
      setIsLoading(false);
      return;
    }
    tracker.login(providerType, viewer);
    startSessionAfterTeamSelect(team);
  };

  const startSessionAfterTeamSelect = (team: TeamListItem) => {
    // チーム選択の場合はオンボードに強制遷移
    if (query.defaultStep === defaultStepJoinTeam && !props.isMobileAppUser) {
      setIsLoading(false);
      window.location.href = onboardPath({
        to: encodeURIComponent(props.to),
      });
      return;
    }

    const options: any = {
      to: props.to,
      teamUid: team.uid,
      providerType,
      tokenType,
    };
    if (!tokenType) {
      setIsLoading(false);
      console.log('tokenType is not exist.');
      return;
    }
    if (!viewer) {
      setIsLoading(false);
      console.log('viewer is not exist.');
      return;
    }
    if (tokenType === TokenType.Password) {
      if (!email || !password) {
        setIsLoading(false);
        console.log('email or password is not exist.');
        return;
      }
      const urlString = getStartSessionUrl({
        ...options,
        email,
        password,
      });

      const url = new URL(decodeURIComponent(urlString));
      // 名前なしかつ、onboardPathを開こうとしていないかつ、アプリ経由ではない場合はonboardを経由するようにする
      if (
        !viewer.name &&
        !url.pathname.endsWith(onboardPath()) &&
        !props.isMobileAppUser
      ) {
        window.location.href = onboardPath({
          to: encodeURIComponent(urlString),
        });
      } else {
        window.location.href = urlString;
      }
    } else {
      if (!token) {
        setIsLoading(false);
        console.log('token is not exist.');
        return;
      }
      const url = getStartSessionUrl({
        ...options,
        token,
      });
      window.location.href = url;
    }
  };

  const onCloseMessage = async () => {
    setMessage(null);
  };

  const onClickResetPassword = async () => {
    setIsResetLoading(true);
    const data = { email };
    const result = await post(apiPath(`/v1/user/send_reset_password`), {
      data,
    });
    if (!result.isSuccess) {
      // setMessage(t('Fail to send reset password'));
    }
    setIsResetLoading(false);
    setMessage(t('Sent reset password'));
    setSeverity(SeverityType.Success);
  };

  const onClickJoinTeam = async (team: TeamListItem) => {
    setIsLoading(true);
    const result = await post(apiPath(`/v1/teams/${team.uid}/users/join`), {
      data: { providerType },
    });
    setIsLoading(false);
    if (!result.isSuccess) {
      setMessage(t('Error Join Team'));
      setSeverity(SeverityType.Error);
      return;
    }
    setActiveStep(Steps.Starting);
    startSessionAfterTeamSelect(team);
  };

  const onSubmitCreateTeam = async () => {
    setIsLoading(true);
    const data: TeamCreateBody = { name: teamName, providerType };
    const createTeamResult = await post(apiPath(`/v1/teams/create`), {
      data,
    });
    if (!createTeamResult.isSuccess) {
      setIsLoading(false);
      setMessage(t('Error Create Team'));
      setSeverity(SeverityType.Error);
      return;
    }
    const team: TeamListItem = createTeamResult.data;
    const createTeamSettingResult = await post(
      apiPath(`/v1/teams/${team.uid}/settings/create`),
      {
        data: {
          isAutoApprovedSignUp: viewerDomain !== null && isCheckedAutoJoin,
          teamDomains: viewerDomain !== null ? [viewerDomain] : [],
        },
      },
    );
    setIsLoading(false);
    if (!createTeamSettingResult.isSuccess) {
      setMessage(t('Error Create Team'));
      setSeverity(SeverityType.Error);
      return;
    }
    setActiveStep(Steps.Starting);
    startSessionAfterTeamSelect(team);
  };

  return {
    state: {
      isLoading,
      isResetLoading,
      activeStep,
      email,
      password,
      loadingProvider,
      providers,
      teams,
      joinTeams,
      teamName,
      isCheckedAutoJoin,
      errors,
      severity,
      message,
      viewerDomain,
    },
    actions: {
      onChange,
      onSubmitEmailLookup,
      onClickTeam,
      onSubmitEmailPassword,
      onClickService,
      onCloseMessage,
      onClickResetPassword,
      onClickJoinTeam,
      onSubmitCreateTeam,
    },
  };
};
